import React, { useState } from 'react';
import { Search, Filter, Plus, FileText } from 'lucide-react';
interface QuotesTabProps {
  onSelectQuote: (id: string) => void;
  quotes: any[];
}
export function QuotesTab({
  onSelectQuote,
  quotes
}: QuotesTabProps) {
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const statuses = ['All', 'Draft', 'Sent', 'Held', 'Contracted', 'Expired'];
  const filteredQuotes = quotes.filter(q => {
    const matchesStatus = filter === 'All' || q.status === filter;
    const matchesSearch = q.groupName.toLowerCase().includes(search.toLowerCase()) || q.agency.toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Contracted':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Held':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Sent':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Draft':
        return 'bg-stone-100 text-stone-700 border-stone-200';
      case 'Expired':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-stone-100 text-stone-700 border-stone-200';
    }
  };
  return <div className="space-y-8 animate-in fade-in duration-500 h-full flex flex-col" data-id="element-633">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-634">
        <div data-id="element-635">
          <h1 className="font-serif text-3xl text-stone-900 mb-2" data-id="element-636">Quotes</h1>
          <p className="text-stone-500" data-id="element-637">
            Manage all group quotes across your portfolio.
          </p>
        </div>
        <button className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 text-sm font-medium transition-colors shadow-sm" data-id="element-638">
          <Plus className="w-4 h-4" data-id="element-639" />
          New Quote
        </button>
      </div>

      <div className="bg-white border border-stone-200 shadow-sm flex-1 flex flex-col min-h-0" data-id="element-640">
        {/* Toolbar */}
        <div className="p-4 border-b border-stone-200 flex flex-col sm:flex-row gap-4 justify-between items-center bg-stone-50/50" data-id="element-641">
          <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto pb-2 sm:pb-0" data-id="element-642">
            {statuses.map(s => <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${filter === s ? 'bg-stone-200 text-stone-900' : 'text-stone-600 hover:bg-stone-100'}`} data-id="element-643">
                {s}
              </button>)}
          </div>

          <div className="relative w-full sm:w-64" data-id="element-644">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-645" />
            <input type="text" placeholder="Search groups or agencies..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600/20 focus:border-red-600 transition-all" data-id="element-646" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-auto flex-1" data-id="element-647">
          <table className="w-full text-left text-sm" data-id="element-648">
            <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10" data-id="element-649">
              <tr data-id="element-650">
                <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-651">
                  Quote ID
                </th>
                <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-652">
                  Group / Agency
                </th>
                <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-653">
                  Property
                </th>
                <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-654">
                  Dates & Rooms
                </th>
                <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-655">
                  Value
                </th>
                <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-656">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100" data-id="element-657">
              {filteredQuotes.length > 0 ? filteredQuotes.map(quote => <tr key={quote.id} onClick={() => onSelectQuote(quote.id)} className="hover:bg-stone-50 cursor-pointer transition-colors group" data-id="element-658">
                    <td className="p-4 font-mono text-stone-500 group-hover:text-red-600 transition-colors" data-id="element-659">
                      {quote.id}
                    </td>
                    <td className="p-4" data-id="element-660">
                      <p className="font-medium text-stone-900" data-id="element-661">
                        {quote.groupName}
                      </p>
                      <p className="text-stone-500" data-id="element-662">{quote.agency}</p>
                    </td>
                    <td className="p-4 text-stone-600" data-id="element-663">{quote.property}</td>
                    <td className="p-4" data-id="element-664">
                      <p className="text-stone-900" data-id="element-665">{quote.dates}</p>
                      <p className="text-stone-500" data-id="element-666">{quote.rooms} rooms</p>
                    </td>
                    <td className="p-4 font-mono text-stone-900" data-id="element-667">
                      ${quote.value.toLocaleString()}
                    </td>
                    <td className="p-4" data-id="element-668">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono border ${getStatusColor(quote.status)}`} data-id="element-669">
                        {quote.status}
                      </span>
                    </td>
                  </tr>) : <tr data-id="element-670">
                  <td colSpan={6} className="p-12 text-center text-stone-500" data-id="element-671">
                    <FileText className="w-8 h-8 mx-auto mb-3 text-stone-300" data-id="element-672" />
                    <p data-id="element-673">No quotes found matching your filters.</p>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}